/**
 * @file PlanLibraryDeployModal.jsx
 * @module ui/src/components/admin/PlanLibraryDeployModal
 * @description
 *   3-step deploy modal for CPL package instantiation. Opened by PlanLibraryBrowser
 *   when the user clicks "Review & Deploy" on a package row. Submits to
 *   POST /api/v1/template-library/packages/{packageCode}/deploy and navigates
 *   to /admin/plans/{planId} (PlanWorkspace) on success.
 *
 * @version 1.2.0
 * @since S875
 * @author data-fit LLC · DecisionFlow · S875
 *
 * @architecture
 *   File number  : 64 (new arch -- not in B2 inventory S753)
 *   Justification: CPL spec v2.2 (S869) section UI Component Contracts
 *   Step lock    : D-S863-1 -- 3-step modal structure is locked, do not add or remove steps
 *
 * @wiring
 *   GET  /api/v1/roles                                    -- Step 2 role dropdown
 *   GET  /api/v1/segments                                 -- Step 2 segment dropdown
 *   POST /api/v1/template-library/packages/{code}/deploy  -- Final step submit
 *   navigate /admin/plans/{planId}                        -- success destination (PlanWorkspace)
 *
 * @steps (dynamic — rendered from visibleSteps array, not hardcoded)
 *   Step 1: Package   -- always shown
 *   Step 2: Scope     -- always shown
 *   Step 3: Economics -- always shown (OTE guidance + cap if package has cap)
 *   Step 4: Rate Curve -- shown iff pkg.layer1Config?.rateCurve?.bands?.length > 0
 *   Step 5: Modifiers  -- shown iff package has ≥1 enabled modifier in layer2ConfigDefaults
 *     Progressive disclosure: each modifier is a collapsible card with checkbox.
 *     Pre-seeded modifiers arrive pre-checked + pre-filled. CA confirms or adjusts.
 *     Optional modifiers (kicker, draw) arrive unchecked. CA opts in.
 *     Next disabled until all pre-checked modifiers are confirmed (green check).
 *
 * @post-body-extension (S888 T2-1)
 *   confirmedBands:        [{bandSequence, fromPct, toPct, payoutPct, interpolation}]
 *   confirmedRevTypeRates: {[revenueTypeCode]: {payoutPct, effectiveDate}}
 *   confirmedClawback:     {protectionWindowDays, clawbackPct, effectiveFrom, billingModel}
 *   confirmedMboWeights:   {[mboCode]: weight}
 *   confirmedCap:          {maxPeriodPayout, currency}
 *   confirmedKicker:       {enabled, threshold, multiplier, effectiveFrom} | null
 *   confirmedDraw:         {enabled, drawType, advanceAmount, advanceCurrency, effectiveFrom} | null
 *   All fields optional -- backend falls back to modifier_seed_config certified defaults.
 *
 * @css-contract
 *   Named classes from index.css throughout:
 *     .modifier-manager-card, .modifier-manager-header, .modifier-manager-header-copy,
 *     .modifier-manager-body, .modifier-manager-footer, .modifier-manager-badge--*,
 *     .modifier-manager-inline-error, .modifier-manager-info, .modifier-manager-warning,
 *     .modifier-manager-code, .primary-button, .secondary-button, .icon-button,
 *     .text-button, .skeleton
 *   rgb(var(--color-*)) tokens via style= for layout-only rules only.
 *   Zero hardcoded hex. Zero dark: variants.
 *
 * @prohibitions (permanent -- mastercontext S874)
 *   - Do NOT call POST /template-library/deploy -- use packages/{code}/deploy
 *   - Do NOT label OTE fields "Certified Default OTE" -- label is "Certified OTE Guidance" (D-S864-2)
 *   - Do NOT navigate to /admin/plans/library after deploy -- navigate to /admin/plans/{planId}
 *   - Do NOT set plan_status='approved' at deploy time -- always 'draft' (router handles)
 *   - Do NOT set modifier_applicability_confirmed=TRUE at deploy time (router handles)
 *   - Do NOT hardcode step count -- StepIndicator receives visibleSteps array (S888 T2-1)
 *   - Do NOT hardcode package conditionals -- derive from pkg.layer1Config / pkg.layer2ConfigDefaults
 *   - Do NOT show RevType source mapping in deploy modal -- OD-10 resolved: tenant-level concern
 *     owned by Import Data surface. Show awareness banner in Step 5 instead.
 *   - Do NOT label RevType rates "Certified rates" -- OD-7: use "Recommended defaults — review
 *     and adjust for your selling motion" (plan_config_data_requirements_v1_0.md §21.8)
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Slugify -- identical to NewPlanCreationSheet.jsx for consistency.
// plan.plan_code varchar(120) NN. Slug capped at 40 chars.
// ---------------------------------------------------------------------------
function slugifyPlanName(name) {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

function extractPlanId(payload) {
  return payload?.planId ?? payload?.plan_id ?? null;
}

// ---------------------------------------------------------------------------
// Step indicator -- dynamic: receives visibleSteps array, not hardcoded list.
// S888 T2-1: step count varies by package (3–5 steps).
// currentStep is the 1-based index into visibleSteps.
// ---------------------------------------------------------------------------
function StepIndicator({ currentStep, visibleSteps }) {
  const steps = visibleSteps;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0',
        padding: '16px 20px',
        borderBottom: '1px solid rgb(var(--color-border))',
      }}
    >
      {steps.map((label, idx) => {
        const stepNum = idx + 1;
        const isComplete = currentStep > stepNum;
        const isActive   = currentStep === stepNum;
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 600,
                  flexShrink: 0,
                  background: isComplete
                    ? 'rgb(var(--color-success))'
                    : isActive
                    ? 'rgb(var(--color-primary))'
                    : 'rgb(var(--color-surface-muted))',
                  color: isComplete || isActive
                    ? 'rgb(var(--color-bg))'
                    : 'rgb(var(--color-text-muted))',
                  border: isActive
                    ? 'none'
                    : isComplete
                    ? 'none'
                    : '1px solid rgb(var(--color-border))',
                }}
              >
                {isComplete ? '✓' : stepNum}
              </div>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive
                    ? 'rgb(var(--color-text))'
                    : 'rgb(var(--color-text-muted))',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: '1px',
                  margin: '0 8px',
                  background: currentStep > stepNum
                    ? 'rgb(var(--color-success))'
                    : 'rgb(var(--color-border))',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field row -- consistent label + input layout used in Steps 2 and 3
// ---------------------------------------------------------------------------
function FieldRow({ label, required, error, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: 'rgb(var(--color-text))',
        }}
      >
        {label}
        {required && (
          <span style={{ color: 'rgb(var(--color-danger))', marginLeft: '2px' }}>*</span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p style={{ margin: 0, fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}>
          {hint}
        </p>
      )}
      {error && (
        <p style={{ margin: 0, fontSize: '11px', color: 'rgb(var(--color-danger))' }}>
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared input style -- consistent with NewPlanCreationSheet pattern
// ---------------------------------------------------------------------------
function inputStyle(hasError) {
  return {
    width: '100%',
    padding: '7px 10px',
    border: `1px solid rgb(var(${hasError ? '--color-danger' : '--color-border'}))`,
    borderRadius: '8px',
    background: 'rgb(var(--color-bg))',
    color: 'rgb(var(--color-text))',
    fontSize: '12px',
  };
}

// ---------------------------------------------------------------------------
// OTE currency formatter
// ---------------------------------------------------------------------------
function fmtOte(val) {
  if (val === null || val === undefined || val === '') return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

// ---------------------------------------------------------------------------
// STEP 1 -- Package Confirm
// Read-only: mandate, applicability, notRecommendedFor, module list
// ---------------------------------------------------------------------------
function Step1PackageConfirm({ pkg, onNext, onClose }) {
  const chips = pkg.layer2ConfigDefaults
    ? Object.entries(pkg.layer2ConfigDefaults)
        .filter(([, v]) => v === true || v === 'enabled')
        .map(([k]) =>
          k.replace(/_enabled$/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        )
    : [];

  return (
    <>
      <div className="modifier-manager-body" style={{ gap: '16px' }}>

        {/* Package identity */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'rgb(var(--color-text))' }}>
              {pkg.packageName || pkg.packageCode}
            </div>
            {pkg.salesMotion && (
              <div style={{ marginTop: '2px', fontSize: '12px', color: 'rgb(var(--color-text-muted))' }}>
                {pkg.salesMotion}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            {pkg.tier && (
              <span className="modifier-manager-badge modifier-manager-badge--not-created">
                {String(pkg.tier).toUpperCase()}
              </span>
            )}
            <span className="modifier-manager-badge modifier-manager-badge--complete">
              Curated
            </span>
          </div>
        </div>

        {/* Mandate */}
        {pkg.mandate && (
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>
              Mandate
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.5 }}>
              {pkg.mandate}
            </p>
          </div>
        )}

        {/* Applicability */}
        {pkg.applicability && (
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>
              Recommended For
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.5 }}>
              {pkg.applicability}
            </p>
          </div>
        )}

        {/* Not recommended for */}
        {pkg.notRecommendedFor && (
          <div className="modifier-manager-warning">
            <strong style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>
              Not recommended for
            </strong>
            {pkg.notRecommendedFor}
          </div>
        )}

        {/* Module list */}
        {chips.length > 0 && (
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgb(var(--color-text-muted))', marginBottom: '6px' }}>
              Included Modules
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {chips.map((chip) => (
                <span key={chip} className="modifier-manager-accelerator-badge modifier-manager-accelerator-badge--active">
                  {chip}
                </span>
              ))}
            </div>
          </div>
        )}

        {chips.length === 0 && (
          <div className="modifier-manager-info">
            No additional modules configured for this package.
          </div>
        )}

      </div>

      {/* Footer */}
      <div className="modifier-manager-footer" style={{ padding: '12px 16px' }}>
        <button type="button" className="secondary-button" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="primary-button" onClick={onNext}>
          Review &amp; Deploy →
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STEP 2 -- Scope
// planName, planCode (auto-slug), roleId, segmentId, fiscalPeriodId, businessUnitId
// ---------------------------------------------------------------------------
function Step2Scope({
  fields, errors, roles, segments, fiscalPeriods, periodsLoading, businessUnits,
  rolesLoading, segmentsLoading, bizUnitsLoading,
  planCodeManuallyEdited, onFieldChange, onPlanNameChange, onPlanCodeChange,
  onBack, onNext, submitting,
}) {
  return (
    <>
      <div className="modifier-manager-body" style={{ gap: '16px' }}>

        <FieldRow label="Plan Name" required error={errors.planName}>
          <input
            type="text"
            placeholder="e.g. Q1 FY2026 AE Enterprise ARR"
            value={fields.planName}
            disabled={submitting}
            onChange={(e) => onPlanNameChange(e.target.value)}
            style={inputStyle(!!errors.planName)}
          />
        </FieldRow>

        <FieldRow
          label="Plan Code"
          required
          error={errors.planCode}
          hint="Auto-generated from plan name. Edit to customise."
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="text"
              placeholder="e.g. Q1_FY2026_AE_ENT_ARR"
              value={fields.planCode}
              disabled={submitting}
              onChange={(e) => onPlanCodeChange(e.target.value)}
              style={{ ...inputStyle(!!errors.planCode), fontFamily: 'ui-monospace, "IBM Plex Mono", monospace', fontSize: '11px' }}
            />
            {planCodeManuallyEdited && (
              <span className="modifier-manager-badge modifier-manager-badge--readonly" style={{ whiteSpace: 'nowrap' }}>
                edited
              </span>
            )}
          </div>
        </FieldRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

          <FieldRow label="Role" required error={errors.roleId}>
            {rolesLoading ? (
              <div className="skeleton" style={{ height: '34px', borderRadius: '8px' }} />
            ) : (
              <select
                value={fields.roleId}
                disabled={submitting}
                onChange={(e) => onFieldChange('roleId', e.target.value)}
                style={inputStyle(!!errors.roleId)}
              >
                <option value="">Select role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={String(r.id)}>{r.name}</option>
                ))}
              </select>
            )}
            {!rolesLoading && roles.length === 0 && (
              <p style={{ margin: 0, fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}>
                No active roles found.
              </p>
            )}
          </FieldRow>

          <FieldRow label="Segment" required error={errors.segmentId}>
            {segmentsLoading ? (
              <div className="skeleton" style={{ height: '34px', borderRadius: '8px' }} />
            ) : (
              <select
                value={fields.segmentId}
                disabled={submitting}
                onChange={(e) => onFieldChange('segmentId', e.target.value)}
                style={inputStyle(!!errors.segmentId)}
              >
                <option value="">Select segment…</option>
                {segments.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.name}</option>
                ))}
              </select>
            )}
            {!segmentsLoading && segments.length === 0 && (
              <p style={{ margin: 0, fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}>
                No active segments found.
              </p>
            )}
          </FieldRow>

        </div>

        <FieldRow label="Fiscal Period" hint="Pre-filled to the current active period. Change to deploy into a different open period.">
          {periodsLoading ? (
            <div className="skeleton" style={{ height: '34px', borderRadius: '8px' }} />
          ) : fiscalPeriods.length > 0 ? (
            <select
              value={fields.fiscalPeriodId}
              disabled={submitting}
              onChange={(e) => onFieldChange('fiscalPeriodId', e.target.value)}
              style={inputStyle(false)}
            >
              {fiscalPeriods.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.label}</option>
              ))}
            </select>
          ) : (
            /* Fetch failed or returned empty -- show read-only pre-filled value */
            <input
              type="text"
              value={fields.fiscalPeriodId ? `Period ${fields.fiscalPeriodId}` : '—'}
              disabled
              style={{ ...inputStyle(false), opacity: 0.6, cursor: 'not-allowed' }}
            />
          )}
        </FieldRow>

        <FieldRow label="Business Unit" hint="Optional — leave blank if not applicable.">
          {bizUnitsLoading ? (
            <div className="skeleton" style={{ height: '34px', borderRadius: '8px' }} />
          ) : businessUnits.length > 0 ? (
            <select
              value={fields.businessUnitId}
              disabled={submitting}
              onChange={(e) => onFieldChange('businessUnitId', e.target.value)}
              style={inputStyle(false)}
            >
              <option value="">None (optional)…</option>
              {businessUnits.map((b) => (
                <option key={b.id} value={String(b.id)}>{b.name}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder="Business unit (optional)…"
              value={fields.businessUnitId}
              disabled={submitting}
              onChange={(e) => onFieldChange('businessUnitId', e.target.value)}
              style={inputStyle(false)}
            />
          )}
        </FieldRow>

      </div>

      <div className="modifier-manager-footer" style={{ padding: '12px 16px' }}>
        <button type="button" className="text-button" disabled={submitting} onClick={onBack}>
          ← Back
        </button>
        <button type="button" className="primary-button" disabled={submitting} onClick={onNext}>
          Next: Economics →
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STEP 3 -- Economics
// oteMin/Midpoint/Max pre-filled from package. Live delta summary.
// ---------------------------------------------------------------------------
function Step3Economics({
  fields, errors, certifiedDefaults, onFieldChange,
  onBack, onNext, onSubmit, submitting, submitError,
}) {
  // onNext: advance to Step 4/5 (more steps follow)
  // onSubmit: deploy directly (no further steps)
  // Count how many OTE values differ from certified defaults
  const changedCount = ['oteMin', 'oteMidpoint', 'oteMax'].filter((key) => {
    const defaultVal = String(certifiedDefaults[key] ?? '');
    const currentVal = String(fields[key] ?? '');
    return defaultVal !== currentVal && currentVal !== '';
  }).length;

  const deployAs = changedCount > 0 ? 'Modified' : 'Certified';

  return (
    <>
      <div className="modifier-manager-body" style={{ gap: '16px' }}>

        {/* Section label -- locked: "Certified OTE Guidance" (D-S864-2) */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(var(--color-text))', marginBottom: '2px' }}>
            Certified OTE Guidance
          </div>
          <p style={{ margin: 0, fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}>
            Advisory only — does not affect payout calculation.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>

          <FieldRow label="OTE Min" error={errors.oteMin}>
            <input
              type="number"
              min={0}
              step={1000}
              placeholder={fmtOte(certifiedDefaults.oteMin)}
              value={fields.oteMin}
              disabled={submitting}
              onChange={(e) => onFieldChange('oteMin', e.target.value)}
              style={inputStyle(!!errors.oteMin)}
            />
          </FieldRow>

          <FieldRow label="OTE Midpoint" error={errors.oteMidpoint}>
            <input
              type="number"
              min={0}
              step={1000}
              placeholder={fmtOte(certifiedDefaults.oteMidpoint)}
              value={fields.oteMidpoint}
              disabled={submitting}
              onChange={(e) => onFieldChange('oteMidpoint', e.target.value)}
              style={inputStyle(!!errors.oteMidpoint)}
            />
          </FieldRow>

          <FieldRow label="OTE Max" error={errors.oteMax}>
            <input
              type="number"
              min={0}
              step={1000}
              placeholder={fmtOte(certifiedDefaults.oteMax)}
              value={fields.oteMax}
              disabled={submitting}
              onChange={(e) => onFieldChange('oteMax', e.target.value)}
              style={inputStyle(!!errors.oteMax)}
            />
          </FieldRow>

        </div>

        {/* Live delta summary -- mandatory per spec */}
        <div
          className={changedCount > 0 ? 'modifier-manager-warning' : 'modifier-manager-info'}
        >
          {changedCount > 0
            ? `You've changed ${changedCount} value${changedCount > 1 ? 's' : ''} from certified defaults. This plan will deploy as `
            : 'No values changed from certified defaults. This plan will deploy as '}
          <strong>{deployAs}</strong>.
        </div>

        {/* Submit error */}
        {submitError && (
          <div role="alert" className="modifier-manager-inline-error">
            {submitError}
          </div>
        )}

        {/* Submitting progress bar */}
        {submitting && (
          <div
            style={{
              height: '4px',
              borderRadius: '999px',
              overflow: 'hidden',
              background: 'rgb(var(--color-surface-muted))',
            }}
            aria-label="Deploying plan"
          >
            <div className="skeleton" style={{ height: '100%', width: '100%' }} />
          </div>
        )}

      </div>

      <div className="modifier-manager-footer" style={{ padding: '12px 16px' }}>
        <button type="button" className="text-button" disabled={submitting} onClick={onBack}>
          ← Back
        </button>
        {onNext ? (
          <button type="button" className="primary-button" disabled={submitting} onClick={onNext}>
            Next →
          </button>
        ) : (
          <button
            type="button"
            className="primary-button"
            disabled={submitting}
            onClick={onSubmit}
          >
            {submitting ? 'Deploying…' : 'Deploy Plan →'}
          </button>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STEP 4 -- Rate Curve Confirmation
// Shown iff pkg has rateCurve bands. Pre-filled, CA confirms or adjusts.
// Uses .modifier-manager-table for band rows, .modifier-manager-percent-input for editable cells.
// ---------------------------------------------------------------------------
function Step4RateCurve({ bands, onBandChange, onBack, onNext, submitting }) {
  const INTERP_OPTIONS = ['step', 'linear'];

  return (
    <>
      <div className="modifier-manager-body" style={{ gap: '16px' }}>

        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(var(--color-text))', marginBottom: '2px' }}>
            Attainment Rate Curve
          </div>
          <p style={{ margin: 0, fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}>
            Pre-filled from certified package defaults. Adjust individual bands before deploying — changes apply to this plan only.
          </p>
        </div>

        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th className="modifier-manager-band-number">#</th>
                <th>From %</th>
                <th>To %</th>
                <th>Payout %</th>
                <th>Interpolation</th>
              </tr>
            </thead>
            <tbody>
              {bands.map((band, idx) => (
                <tr key={idx}>
                  <td className="modifier-manager-band-number">{band.bandSequence}</td>
                  <td>
                    <div className="modifier-manager-percent-input">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={band.fromPct}
                        disabled={submitting}
                        aria-label={`Band ${band.bandSequence} from percent`}
                        onChange={(e) => onBandChange(idx, 'fromPct', e.target.value)}
                      />
                      <span>%</span>
                    </div>
                  </td>
                  <td>
                    <div className="modifier-manager-percent-input">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={band.toPct}
                        disabled={submitting}
                        aria-label={`Band ${band.bandSequence} to percent`}
                        onChange={(e) => onBandChange(idx, 'toPct', e.target.value)}
                      />
                      <span>%</span>
                    </div>
                  </td>
                  <td>
                    <div className="modifier-manager-percent-input">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={band.payoutPct}
                        disabled={submitting}
                        aria-label={`Band ${band.bandSequence} payout percent`}
                        onChange={(e) => onBandChange(idx, 'payoutPct', e.target.value)}
                      />
                      <span>×</span>
                    </div>
                  </td>
                  <td>
                    <select
                      value={band.interpolation}
                      disabled={submitting}
                      aria-label={`Band ${band.bandSequence} interpolation`}
                      onChange={(e) => onBandChange(idx, 'interpolation', e.target.value)}
                    >
                      {INTERP_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modifier-manager-info">
          Bands define attainment multipliers — e.g. 1.00× at 100% attainment means the rep earns their full variable. Accelerator bands above 1.00× are additive to base payout.
        </div>

      </div>

      <div className="modifier-manager-footer" style={{ padding: '12px 16px' }}>
        <button type="button" className="text-button" disabled={submitting} onClick={onBack}>
          ← Back
        </button>
        <button type="button" className="primary-button" disabled={submitting} onClick={onNext}>
          Next: Modifiers →
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// MODIFIER CARD -- single collapsible modifier unit used inside Step5Modifiers.
// Pre-seeded modifiers: pre-checked, pre-filled, collapsed with summary line.
// Optional modifiers: unchecked, no fields visible until checked.
// CA clicks [Edit] / expands → fills fields → [Confirm] → green check + summary.
// ---------------------------------------------------------------------------

// RevType card
function RevTypeCard({ draft, onChange, onConfirm, confirmed, submitting }) {
  const [expanded, setExpanded] = useState(!confirmed);
  const revTypes = draft.rates ? Object.keys(draft.rates) : [];

  const summaryLine = revTypes.map((rt) => {
    const r = draft.rates[rt];
    return `${rt.replace(/_/g, ' ')}: ${r.payoutPct}×`;
  }).join(' · ');

  return (
    <div className="modifier-manager-card">
      <div className="modifier-manager-header" style={{ cursor: 'pointer' }} onClick={() => !submitting && setExpanded((v) => !v)}>
        <div className="modifier-manager-header-copy">
          <h3>
            {confirmed && <span style={{ color: 'rgb(var(--color-success))', marginRight: '6px' }}>✓</span>}
            Revenue Type Differentials
          </h3>
          {confirmed && !expanded && (
            <p>{summaryLine}</p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {confirmed
            ? <span className="modifier-manager-badge modifier-manager-badge--complete">Confirmed</span>
            : <span className="modifier-manager-badge modifier-manager-badge--incomplete">Review required</span>
          }
          <button
            type="button"
            className="icon-button"
            disabled={submitting}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          >
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="modifier-manager-body" style={{ gap: '12px' }}>
          {/* OD-7: "Recommended defaults" language per plan_config_data_requirements §21.8 */}
          <div className="modifier-manager-info">
            <strong>Recommended defaults — review and adjust for your selling motion.</strong>
            <br />
            These are suggested starting values based on common enterprise B2B SaaS selling motions. Adjust to reflect your organisation's revenue quality economics.
          </div>

          {revTypes.map((rt) => (
            <div key={rt} style={{ border: '1px solid rgb(var(--color-border))', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'rgb(var(--color-text))', textTransform: 'capitalize' }}>
                {rt.replace(/_/g, ' ')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <FieldRow label="Payout multiplier">
                  <div className="modifier-manager-percent-input">
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      value={draft.rates[rt].payoutPct}
                      disabled={submitting}
                      onChange={(e) => {
                        const next = { ...draft, rates: { ...draft.rates, [rt]: { ...draft.rates[rt], payoutPct: e.target.value } } };
                        onChange(next);
                      }}
                    />
                    <span>×</span>
                  </div>
                </FieldRow>
                <FieldRow label="Effective date" required>
                  <input
                    type="date"
                    value={draft.rates[rt].effectiveDate ?? ''}
                    disabled={submitting}
                    style={inputStyle(false)}
                    onChange={(e) => {
                      const next = { ...draft, rates: { ...draft.rates, [rt]: { ...draft.rates[rt], effectiveDate: e.target.value } } };
                      onChange(next);
                    }}
                  />
                </FieldRow>
              </div>
            </div>
          ))}

          {/* RevType awareness banner -- OD-10: source mapping is Import Data surface concern */}
          <div className="modifier-manager-warning">
            Before running a calculation, ensure your credit import source codes are mapped to DecisionFlow revenue types in the <strong>Import Data</strong> surface.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="primary-button"
              disabled={submitting || revTypes.some((rt) => !draft.rates[rt].effectiveDate)}
              onClick={() => { onConfirm(); setExpanded(false); }}
            >
              ✓ Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Clawback card
function ClawbackCard({ draft, onChange, onConfirm, confirmed, submitting, showBillingModel }) {
  const [expanded, setExpanded] = useState(!confirmed);

  const billingWindowMap = { monthly: 90, annual: 180 };

  const summaryLine = confirmed
    ? `${draft.protectionWindowDays} days · ${Math.round((draft.clawbackPct ?? 1) * 100)}% clawback · from ${draft.effectiveFrom || '—'}`
    : '';

  function handleBillingModel(model) {
    onChange({ ...draft, billingModel: model, protectionWindowDays: billingWindowMap[model] });
  }

  return (
    <div className="modifier-manager-card">
      <div className="modifier-manager-header" style={{ cursor: 'pointer' }} onClick={() => !submitting && setExpanded((v) => !v)}>
        <div className="modifier-manager-header-copy">
          <h3>
            {confirmed && <span style={{ color: 'rgb(var(--color-success))', marginRight: '6px' }}>✓</span>}
            Churn Clawback
          </h3>
          {confirmed && !expanded && <p>{summaryLine}</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {confirmed
            ? <span className="modifier-manager-badge modifier-manager-badge--complete">Confirmed</span>
            : <span className="modifier-manager-badge modifier-manager-badge--incomplete">Review required</span>
          }
          <button type="button" className="icon-button" disabled={submitting} aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="modifier-manager-body" style={{ gap: '12px' }}>
          {showBillingModel && (
            <FieldRow label="Billing model" hint="Determines the clawback protection window.">
              <select
                value={draft.billingModel ?? 'monthly'}
                disabled={submitting}
                style={inputStyle(false)}
                onChange={(e) => handleBillingModel(e.target.value)}
              >
                <option value="monthly">Monthly (90-day window)</option>
                <option value="annual">Annual (180-day window)</option>
              </select>
            </FieldRow>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FieldRow label="Protection window (days)" required>
              <input
                type="number"
                min={1}
                value={draft.protectionWindowDays ?? 90}
                disabled={submitting || showBillingModel}
                style={inputStyle(false)}
                onChange={(e) => onChange({ ...draft, protectionWindowDays: Number(e.target.value) })}
              />
            </FieldRow>
            <FieldRow label="Clawback %" required hint="100% = full recapture.">
              <div className="modifier-manager-percent-input">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round((draft.clawbackPct ?? 1) * 100)}
                  disabled={submitting}
                  onChange={(e) => onChange({ ...draft, clawbackPct: Number(e.target.value) / 100 })}
                />
                <span>%</span>
              </div>
            </FieldRow>
          </div>
          <FieldRow label="Effective from" required>
            <input
              type="date"
              value={draft.effectiveFrom ?? ''}
              disabled={submitting}
              style={inputStyle(false)}
              onChange={(e) => onChange({ ...draft, effectiveFrom: e.target.value })}
            />
          </FieldRow>
          <div className="modifier-manager-info">
            Churn events are ingested separately via the Import Data surface after plan approval.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="primary-button"
              disabled={submitting || !draft.effectiveFrom}
              onClick={() => { onConfirm(); setExpanded(false); }}
            >
              ✓ Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// MBO objectives card
function MboCard({ draft, onChange, onConfirm, confirmed, submitting }) {
  const [expanded, setExpanded] = useState(!confirmed);
  const objectives = draft.objectives ?? [];
  const totalWeight = objectives.reduce((s, o) => s + Number(o.weight ?? 0), 0);
  const weightOk = Math.abs(totalWeight - 1) < 0.001;

  const summaryLine = confirmed
    ? objectives.map((o) => `${o.mboName}: ${Math.round(o.weight * 100)}%`).join(' · ')
    : '';

  return (
    <div className="modifier-manager-card">
      <div className="modifier-manager-header" style={{ cursor: 'pointer' }} onClick={() => !submitting && setExpanded((v) => !v)}>
        <div className="modifier-manager-header-copy">
          <h3>
            {confirmed && <span style={{ color: 'rgb(var(--color-success))', marginRight: '6px' }}>✓</span>}
            MBO Objectives
          </h3>
          {confirmed && !expanded && <p>{summaryLine}</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {confirmed
            ? <span className="modifier-manager-badge modifier-manager-badge--complete">Confirmed</span>
            : <span className="modifier-manager-badge modifier-manager-badge--incomplete">Review required</span>
          }
          <button type="button" className="icon-button" disabled={submitting} aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="modifier-manager-body" style={{ gap: '12px' }}>
          <div className="modifier-manager-table-wrapper">
            <table className="modifier-manager-table">
              <thead>
                <tr>
                  <th>Objective</th>
                  <th>Scoring</th>
                  <th>Weight %</th>
                </tr>
              </thead>
              <tbody>
                {objectives.map((obj, idx) => (
                  <tr key={obj.mboCode}>
                    <td style={{ fontWeight: 500 }}>{obj.mboName}</td>
                    <td>
                      <span className="modifier-manager-badge modifier-manager-badge--not-created">
                        {obj.scoringMethod}
                      </span>
                    </td>
                    <td>
                      <div className="modifier-manager-percent-input">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={Math.round(Number(obj.weight) * 100)}
                          disabled={submitting}
                          aria-label={`${obj.mboName} weight`}
                          onChange={(e) => {
                            const next = objectives.map((o, i) =>
                              i === idx ? { ...o, weight: Number(e.target.value) / 100 } : o
                            );
                            onChange({ ...draft, objectives: next });
                          }}
                        />
                        <span>%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!weightOk && (
            <div className="modifier-manager-inline-error">
              Weights must total 100%. Current total: {Math.round(totalWeight * 100)}%.
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="primary-button"
              disabled={submitting || !weightOk}
              onClick={() => { onConfirm(); setExpanded(false); }}
            >
              ✓ Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Optional modifier card (Kicker / Draw) -- unchecked by default
function OptionalModifierCard({ label, description, enabled, onToggle, children, confirmed, submitting }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="modifier-manager-card" style={{ opacity: submitting ? 0.7 : 1 }}>
      <div className="modifier-manager-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
          <input
            type="checkbox"
            id={`opt-${label}`}
            checked={enabled}
            disabled={submitting}
            style={{ marginTop: '2px', cursor: 'pointer' }}
            onChange={(e) => { onToggle(e.target.checked); if (e.target.checked) setExpanded(true); else setExpanded(false); }}
          />
          <label htmlFor={`opt-${label}`} style={{ cursor: 'pointer', flex: 1 }}>
            <div className="modifier-manager-header-copy">
              <h3>
                {confirmed && enabled && <span style={{ color: 'rgb(var(--color-success))', marginRight: '6px' }}>✓</span>}
                {label} <span style={{ fontSize: '10px', color: 'rgb(var(--color-text-muted))', fontWeight: 400, marginLeft: '4px' }}>optional</span>
              </h3>
              <p>{description}</p>
            </div>
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {enabled && confirmed && <span className="modifier-manager-badge modifier-manager-badge--complete">Confirmed</span>}
          {enabled && !confirmed && <span className="modifier-manager-badge modifier-manager-badge--incomplete">Fill in details</span>}
          {!enabled && <span className="modifier-manager-badge modifier-manager-badge--not-created">Not added</span>}
          {enabled && (
            <button type="button" className="icon-button" disabled={submitting} aria-label={expanded ? 'Collapse' : 'Expand'}
              onClick={() => setExpanded((v) => !v)}>
              {expanded ? '▲' : '▼'}
            </button>
          )}
        </div>
      </div>
      {enabled && expanded && children}
    </div>
  );
}

// Kicker fields (inside OptionalModifierCard)
function KickerFields({ draft, onChange, onConfirm, confirmed, submitting }) {
  return (
    <div className="modifier-manager-body" style={{ gap: '12px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <FieldRow label="Attainment threshold %" required hint="e.g. 110 = kicks in at 110% attainment">
          <div className="modifier-manager-percent-input">
            <input
              type="number" min={100} step={5}
              value={draft.threshold ?? 110}
              disabled={submitting}
              onChange={(e) => onChange({ ...draft, threshold: Number(e.target.value) })}
            />
            <span>%</span>
          </div>
        </FieldRow>
        <FieldRow label="Multiplier" required hint="e.g. 1.25 = 25% bonus on total payout">
          <div className="modifier-manager-percent-input">
            <input
              type="number" min={1} step={0.05}
              value={draft.multiplier ?? 1.25}
              disabled={submitting}
              onChange={(e) => onChange({ ...draft, multiplier: Number(e.target.value) })}
            />
            <span>×</span>
          </div>
        </FieldRow>
      </div>
      <FieldRow label="Effective from" required>
        <input type="date" value={draft.effectiveFrom ?? ''} disabled={submitting} style={inputStyle(false)}
          onChange={(e) => onChange({ ...draft, effectiveFrom: e.target.value })} />
      </FieldRow>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="primary-button"
          disabled={submitting || !draft.effectiveFrom}
          onClick={onConfirm}>
          ✓ Confirm
        </button>
      </div>
    </div>
  );
}

// Draw fields (inside OptionalModifierCard) -- recoverable disabled per Bug 3
function DrawFields({ draft, onChange, onConfirm, confirmed, submitting }) {
  return (
    <div className="modifier-manager-body" style={{ gap: '12px' }}>
      <div className="modifier-manager-warning">
        Only non-recoverable draw is available. Recoverable draw requires additional configuration — enable it in the plan workspace after deploy.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <FieldRow label="Advance amount" required>
          <input type="number" min={0} step={100}
            value={draft.advanceAmount ?? ''}
            disabled={submitting}
            style={inputStyle(false)}
            onChange={(e) => onChange({ ...draft, advanceAmount: Number(e.target.value), drawType: 'non_recoverable' })} />
        </FieldRow>
        <FieldRow label="Currency" required>
          <input type="text" maxLength={3} placeholder="USD"
            value={draft.advanceCurrency ?? 'USD'}
            disabled={submitting}
            style={inputStyle(false)}
            onChange={(e) => onChange({ ...draft, advanceCurrency: e.target.value.toUpperCase() })} />
        </FieldRow>
      </div>
      <FieldRow label="Effective from" required>
        <input type="date" value={draft.effectiveFrom ?? ''} disabled={submitting} style={inputStyle(false)}
          onChange={(e) => onChange({ ...draft, effectiveFrom: e.target.value })} />
      </FieldRow>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="primary-button"
          disabled={submitting || !draft.advanceAmount || !draft.effectiveFrom}
          onClick={onConfirm}>
          ✓ Confirm
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// STEP 5 -- Modifier Configuration
// Progressive disclosure: collapsible modifier cards.
// Pre-seeded = pre-checked + pre-filled. Optional = unchecked.
// Next disabled until all required (pre-seeded) modifiers confirmed.
// ---------------------------------------------------------------------------
function Step5Modifiers({
  modifierDrafts, confirmedModifiers, onDraftChange, onConfirm,
  hasRevType, hasClawback, hasMbo, hasKicker, hasDraw, showBillingModel,
  kickerEnabled, drawEnabled, onKickerToggle, onDrawToggle,
  onBack, onNext, submitting,
}) {
  const requiredMods = [
    hasRevType && 'rev_type',
    hasClawback && 'clawback',
    hasMbo && 'mbo',
  ].filter(Boolean);

  const allRequiredConfirmed = requiredMods.every((m) => confirmedModifiers.has(m));
  const optionalKickerOk = !kickerEnabled || confirmedModifiers.has('kicker');
  const optionalDrawOk   = !drawEnabled   || confirmedModifiers.has('draw');
  const canProceed = allRequiredConfirmed && optionalKickerOk && optionalDrawOk;

  const hasAny = hasRevType || hasClawback || hasMbo || hasKicker || hasDraw;

  return (
    <>
      <div className="modifier-manager-body" style={{ gap: '16px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(var(--color-text))', marginBottom: '2px' }}>
            Modifier Configuration
          </div>
          <p style={{ margin: 0, fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}>
            Review pre-filled defaults for each modifier. Confirm each section before deploying.
            Optional adjustments can be added now or configured later in the plan workspace.
          </p>
        </div>

        {!hasAny && (
          <div className="modifier-manager-info">
            No modifiers are configured for this package. Proceed to deploy.
          </div>
        )}

        {hasRevType && (
          <RevTypeCard
            draft={modifierDrafts.revType}
            onChange={(d) => onDraftChange('revType', d)}
            onConfirm={() => onConfirm('rev_type')}
            confirmed={confirmedModifiers.has('rev_type')}
            submitting={submitting}
          />
        )}

        {hasClawback && (
          <ClawbackCard
            draft={modifierDrafts.clawback}
            onChange={(d) => onDraftChange('clawback', d)}
            onConfirm={() => onConfirm('clawback')}
            confirmed={confirmedModifiers.has('clawback')}
            submitting={submitting}
            showBillingModel={showBillingModel}
          />
        )}

        {hasMbo && (
          <MboCard
            draft={modifierDrafts.mbo}
            onChange={(d) => onDraftChange('mbo', d)}
            onConfirm={() => onConfirm('mbo')}
            confirmed={confirmedModifiers.has('mbo')}
            submitting={submitting}
          />
        )}

        {hasKicker && (
          <OptionalModifierCard
            label="Kicker / Multiplier"
            description="Bonus multiplier applied to total payout when a performance threshold is exceeded."
            enabled={kickerEnabled}
            onToggle={onKickerToggle}
            confirmed={confirmedModifiers.has('kicker')}
            submitting={submitting}
          >
            <KickerFields
              draft={modifierDrafts.kicker}
              onChange={(d) => onDraftChange('kicker', d)}
              onConfirm={() => onConfirm('kicker')}
              confirmed={confirmedModifiers.has('kicker')}
              submitting={submitting}
            />
          </OptionalModifierCard>
        )}

        {hasDraw && (
          <OptionalModifierCard
            label="Draw / Guarantee"
            description="Guaranteed floor payment — non-recoverable advance against future earnings."
            enabled={drawEnabled}
            onToggle={onDrawToggle}
            confirmed={confirmedModifiers.has('draw')}
            submitting={submitting}
          >
            <DrawFields
              draft={modifierDrafts.draw}
              onChange={(d) => onDraftChange('draw', d)}
              onConfirm={() => onConfirm('draw')}
              confirmed={confirmedModifiers.has('draw')}
              submitting={submitting}
            />
          </OptionalModifierCard>
        )}

      </div>

      <div className="modifier-manager-footer" style={{ padding: '12px 16px' }}>
        <button type="button" className="text-button" disabled={submitting} onClick={onBack}>
          ← Back
        </button>
        <button
          type="button"
          className="primary-button"
          disabled={submitting || !canProceed}
          title={!canProceed ? 'Confirm all required modifiers above to proceed.' : undefined}
          onClick={onNext}
        >
          Deploy Plan →
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main modal component
// ---------------------------------------------------------------------------

const EMPTY_SCOPE = {
  planName:       '',
  planCode:       '',
  roleId:         '',
  segmentId:      '',
  businessUnitId: '',
  fiscalPeriodId: '',
};

const EMPTY_SCOPE_ERRORS = {
  planName:  '',
  planCode:  '',
  roleId:    '',
  segmentId: '',
};

const EMPTY_ECON_ERRORS = {
  oteMin:      '',
  oteMidpoint: '',
  oteMax:      '',
};

// ---------------------------------------------------------------------------
// Derive which optional steps are visible for this package.
// OD-8: Step 4 (Rate Curve) shown iff layer1Config.rateCurve.bands is non-empty.
// OD-9: Step 5 (Modifiers) shown iff ≥1 modifier is enabled in layer2ConfigDefaults.
// OD-10: No Step 6 (RevType source mapping) -- tenant-level concern, Import Data surface.
// Returns array of step label strings in display order.
// ---------------------------------------------------------------------------
function computeVisibleSteps(pkg) {
  const steps = ['Package', 'Scope', 'Economics'];
  const hasBands = pkg.layer1Config?.rateCurve?.bands?.length > 0
    || pkg.rateCurve?.bands?.length > 0;
  if (hasBands) steps.push('Rate Curve');
  const l2Defaults = Array.isArray(pkg.layer2ConfigDefaults) ? pkg.layer2ConfigDefaults : Object.values(pkg.layer2ConfigDefaults ?? {});
  const enabledMods = l2Defaults.filter((m) => m.enabled);
  if (enabledMods.length > 0) steps.push('Modifiers');
  return steps;
}

// ---------------------------------------------------------------------------
// Derive modifier presence flags from package layer2ConfigDefaults.
// ---------------------------------------------------------------------------
function deriveModifierFlags(pkg) {
  const defaults = Array.isArray(pkg.layer2ConfigDefaults) ? pkg.layer2ConfigDefaults : Object.values(pkg.layer2ConfigDefaults ?? {});
  const enabledTypes = new Set(defaults.filter((m) => m.enabled).map((m) => m.modifierType || m.modifier_type));
  const seed = pkg.modifierSeedConfig ?? pkg.modifier_seed_config ?? {};
  return {
    hasRevType:       enabledTypes.has('rev_type_differentials'),
    hasClawback:      enabledTypes.has('clawback'),
    hasMbo:           enabledTypes.has('mbo_definition') || enabledTypes.has('mbo'),
    hasKicker:        enabledTypes.has('kicker'),
    hasDraw:          enabledTypes.has('draw_guarantee'),
    // Package 6 billing model selector (protectionWindowDays driven by monthly/annual)
    showBillingModel: (pkg.packageCode ?? '') === 'renewal-ae',
    seed,
  };
}

// ---------------------------------------------------------------------------
// Build initial modifier drafts from package seed config / layer2ConfigDefaults.
// Pre-fills certified defaults so CA is confirming, not building from scratch.
// ---------------------------------------------------------------------------
function buildModifierDrafts(pkg, effectiveDateStr) {
  const seed = pkg.modifierSeedConfig ?? pkg.modifier_seed_config ?? {};
  const layer2 = Array.isArray(pkg.layer2ConfigDefaults) ? pkg.layer2ConfigDefaults : Object.values(pkg.layer2ConfigDefaults ?? {});

  // RevType: seed is {rev_type_code: payout_multiplier} or structured object
  const revTypeSeed = seed.rev_type_differentials ?? {};
  const revTypeRates = {};
  Object.entries(revTypeSeed).forEach(([code, val]) => {
    revTypeRates[code] = {
      payoutPct: typeof val === 'object' ? (val.payoutPct ?? val.payout_pct ?? val) : val,
      effectiveDate: effectiveDateStr ?? '',
    };
  });

  // Clawback
  const cbSeed = seed.clawback ?? {};
  const clawbackDraft = {
    protectionWindowDays: cbSeed.protection_window_days ?? cbSeed.protectionWindowDays ?? 90,
    clawbackPct:          cbSeed.clawback_pct          ?? cbSeed.clawbackPct          ?? 1.0,
    effectiveFrom:        effectiveDateStr ?? '',
    billingModel:         'monthly',
  };

  // MBO: build from layer2ConfigDefaults mbo element draftConfig or fallback structure
  const mboElement = layer2.find((m) => (m.modifierType || m.modifier_type) === 'mbo_definition'
    || (m.modifierType || m.modifier_type) === 'mbo');
  const mboSeed = seed.mbo_definition ?? mboElement?.draftConfig ?? {};
  // If mboSeed has objectives array use it; otherwise build from mboObjectives key
  const rawObjectives = mboSeed.objectives ?? mboSeed.mboObjectives ?? [];
  const mboDraft = {
    objectives: rawObjectives.map((o) => ({
      mboCode:       o.mboCode       ?? o.mbo_code,
      mboName:       o.mboName       ?? o.mbo_name,
      weight:        o.weight        ?? o.mbo_weight,
      scoringMethod: o.scoringMethod ?? o.scoring_method,
    })),
  };

  // Kicker / Draw optional drafts (empty defaults -- CA fills if they enable)
  const kickerDraft = { threshold: 110, multiplier: 1.25, effectiveFrom: effectiveDateStr ?? '' };
  const drawDraft   = { drawType: 'non_recoverable', advanceAmount: '', advanceCurrency: 'USD', effectiveFrom: effectiveDateStr ?? '' };

  return { revType: { rates: revTypeRates }, clawback: clawbackDraft, mbo: mboDraft, kicker: kickerDraft, draw: drawDraft };
}

// ---------------------------------------------------------------------------
// Build initial rate curve bands from package layer1Config.rateCurve.
// ---------------------------------------------------------------------------
function buildBandDrafts(pkg) {
  const rateCurve = pkg.layer1Config?.rateCurve ?? pkg.rateCurve ?? {};
  const bands = rateCurve.bands ?? [];
  return bands.map((b, idx) => ({
    bandSequence:  b.band_sequence ?? b.bandSequence ?? (idx + 1),
    fromPct:       b.from_pct      ?? b.fromPct      ?? b.from ?? 0,
    toPct:         b.to_pct        ?? b.toPct         ?? b.to   ?? 0,
    payoutPct:     b.payout_pct    ?? b.payoutPct     ?? b.rate ?? 0,
    interpolation: b.interpolation ?? 'step',
    isAccelerator: b.is_accelerator ?? b.isAccelerator ?? false,
  }));
}

/**
 * 3-step CPL deploy modal. Structure locked D-S863-1.
 *
 * @param {{
 *   pkg: object,               -- full normalised package from PlanLibraryBrowser
 *   fiscalPeriodId: number,    -- current fiscal period, pre-fills Step 2
 *   onClose: () => void,
 *   onDeploySuccess?: () => void, -- optional: called after successful deploy, before navigate.
 *                                    PlanLibraryBrowser passes fetchPackages here so the
 *                                    Status badge refreshes without a full page reload.
 *                                    Bug fix S936: badge was permanently "Not Deployed" after
 *                                    deploy because no refetch path existed.
 * }} props
 */
export default function PlanLibraryDeployModal({ pkg, fiscalPeriodId, onClose, onDeploySuccess }) {
  const navigate = useNavigate();

  // ── Derive package capabilities once -- drives step visibility and modifier panels ──
  const visibleSteps    = computeVisibleSteps(pkg);
  const totalSteps      = visibleSteps.length;
  const hasStep4        = visibleSteps.includes('Rate Curve');
  const hasStep5        = visibleSteps.includes('Modifiers');
  const modFlags        = deriveModifierFlags(pkg);

  // step is 1-based index into visibleSteps
  const [step, setStep] = useState(1);

  // ── Step 2 -- Scope state ───────────────────────────────────────────────
  const [scopeFields, setScopeFields] = useState({
    ...EMPTY_SCOPE,
    fiscalPeriodId: fiscalPeriodId ? String(fiscalPeriodId) : '',
  });
  const [scopeErrors, setScopeErrors]                       = useState(EMPTY_SCOPE_ERRORS);
  const [planCodeManuallyEdited, setPlanCodeManuallyEdited] = useState(false);

  // ── Step 3 -- Economics state ───────────────────────────────────────────
  const [econFields, setEconFields] = useState({
    oteMin:      pkg.oteMin      != null ? String(pkg.oteMin)      : '',
    oteMidpoint: pkg.oteMidpoint != null ? String(pkg.oteMidpoint) : '',
    oteMax:      pkg.oteMax      != null ? String(pkg.oteMax)      : '',
  });
  const [econErrors, setEconErrors] = useState(EMPTY_ECON_ERRORS);

  const certifiedDefaults = {
    oteMin:      pkg.oteMin,
    oteMidpoint: pkg.oteMidpoint,
    oteMax:      pkg.oteMax,
  };

  // ── Step 4 -- Rate Curve state ──────────────────────────────────────────
  const [bandDrafts, setBandDrafts] = useState(() => buildBandDrafts(pkg));

  // ── Step 5 -- Modifier state ────────────────────────────────────────────
  // effectiveDateStr derives from fiscalPeriodId prop as ISO fallback until Step 2 fetch resolves
  const effectiveDateStr = '';  // populated at deploy time from scopeFields; drafts use '' as default
  const [modifierDrafts, setModifierDrafts]         = useState(() => buildModifierDrafts(pkg, effectiveDateStr));
  const [confirmedModifiers, setConfirmedModifiers] = useState(new Set());
  const [kickerEnabled, setKickerEnabled]           = useState(false);
  const [drawEnabled, setDrawEnabled]               = useState(false);

  // ── Lookups: roles, segments, business units, fiscal periods ───────────
  const [roles, setRoles]                   = useState([]);
  const [segments, setSegments]             = useState([]);
  const [businessUnits, setBusinessUnits]   = useState([]);
  const [fiscalPeriods, setFiscalPeriods]   = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [rolesLoading, setRolesLoading]     = useState(false);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [bizUnitsLoading, setBizUnitsLoading] = useState(false);

  // ── Submit ──────────────────────────────────────────────────────────────
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const abortRolesRef    = useRef(null);
  const abortSegmentsRef = useRef(null);
  const abortBizUnitsRef = useRef(null);
  const abortPeriodsRef  = useRef(null);
  const abortDeployRef   = useRef(null);

  // ── Fetch roles + segments when Step 2 first mounts ─────────────────────
  useEffect(() => {
    if (step !== 2) return;

    // Roles
    abortRolesRef.current?.abort();
    const rc = new AbortController();
    abortRolesRef.current = rc;
    setRolesLoading(true);
    fetch('/api/v1/roles', { credentials: 'include', signal: rc.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`roles ${r.status}`)))
      .then((payload) => {
        if (rc.signal.aborted) return;
        const raw = Array.isArray(payload) ? payload : payload.data ?? payload.roles ?? payload.items ?? [];
        setRoles(raw.map((r) => ({ id: r.role_id ?? r.id, name: r.role_name ?? r.name ?? '' })));
      })
      .catch(() => {})
      .finally(() => { if (!rc.signal.aborted) setRolesLoading(false); });

    // Segments
    abortSegmentsRef.current?.abort();
    const sc = new AbortController();
    abortSegmentsRef.current = sc;
    setSegmentsLoading(true);
    fetch('/api/v1/segments', { credentials: 'include', signal: sc.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`segments ${r.status}`)))
      .then((payload) => {
        if (sc.signal.aborted) return;
        const raw = Array.isArray(payload) ? payload : payload.data ?? payload.segments ?? payload.items ?? [];
        setSegments(raw.map((s) => ({ id: s.segment_id ?? s.id, name: s.segment_name ?? s.name ?? '' })));
      })
      .catch(() => {})
      .finally(() => { if (!sc.signal.aborted) setSegmentsLoading(false); });

    // Business Units
    abortBizUnitsRef.current?.abort();
    const bc = new AbortController();
    abortBizUnitsRef.current = bc;
    setBizUnitsLoading(true);
    fetch('/api/v1/business-units', { credentials: 'include', signal: bc.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`business-units ${r.status}`)))
      .then((payload) => {
        if (bc.signal.aborted) return;
        const raw = Array.isArray(payload) ? payload
          : payload.data ?? payload.business_units ?? payload.items ?? [];
        setBusinessUnits(raw.map((b) => ({
          id:   b.business_unit_id ?? b.id,
          name: b.bu_name ?? b.business_unit_name ?? b.name ?? '',
        })));
      })
      .catch(() => { /* endpoint may not exist yet — degrade to text input */ })
      .finally(() => { if (!bc.signal.aborted) setBizUnitsLoading(false); });

    // Fiscal Periods -- fetch open periods for dropdown; pre-select active if not already set
    abortPeriodsRef.current?.abort();
    const pc = new AbortController();
    abortPeriodsRef.current = pc;
    setPeriodsLoading(true);
    fetch('/api/v1/fiscal-periods?status=active', { credentials: 'include', signal: pc.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`fiscal-periods ${r.status}`)))
      .then((payload) => {
        if (pc.signal.aborted) return;
        const raw = Array.isArray(payload) ? payload
          : payload.data ?? payload.fiscal_periods ?? payload.items ?? [];
        const normalized = raw.map((p) => ({
          id:    p.fiscal_period_id ?? p.id,
          label: p.period_label ?? p.period_code ?? p.name ?? String(p.fiscal_period_id ?? p.id),
        }));
        setFiscalPeriods(normalized);
        // Pre-select: if scope field is empty or still set to the prop value, confirm it's valid
        // If no match found for the prop ID, default to first available open period
        setScopeFields((prev) => {
          const currentId = prev.fiscalPeriodId;
          const match = normalized.find((p) => String(p.id) === String(currentId));
          if (match) return prev; // already valid — keep
          // Fall back to first open period in the list
          const first = normalized[0];
          return first ? { ...prev, fiscalPeriodId: String(first.id) } : prev;
        });
      })
      .catch(() => { /* leave pre-filled prop value in place */ })
      .finally(() => { if (!pc.signal.aborted) setPeriodsLoading(false); });

    return () => {
      abortRolesRef.current?.abort();
      abortSegmentsRef.current?.abort();
      abortBizUnitsRef.current?.abort();
      abortPeriodsRef.current?.abort();
    };
  }, [step]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      abortRolesRef.current?.abort();
      abortSegmentsRef.current?.abort();
      abortBizUnitsRef.current?.abort();
      abortPeriodsRef.current?.abort();
      abortDeployRef.current?.abort();
    };
  }, []);

  // ── Scope field handlers ─────────────────────────────────────────────────
  function handlePlanNameChange(value) {
    setScopeFields((prev) => {
      const updated = { ...prev, planName: value };
      if (!planCodeManuallyEdited) {
        updated.planCode = slugifyPlanName(value);
      }
      return updated;
    });
    // Clear both planName and planCode errors -- auto-slug may have satisfied planCode requirement
    setScopeErrors((p) => ({ ...p, planName: '', planCode: '' }));
  }

  function handlePlanCodeChange(value) {
    setPlanCodeManuallyEdited(true);
    setScopeFields((prev) => ({
      ...prev,
      planCode: value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''),
    }));
    if (scopeErrors.planCode) setScopeErrors((p) => ({ ...p, planCode: '' }));
  }

  function handleScopeFieldChange(field, value) {
    setScopeFields((prev) => ({ ...prev, [field]: value }));
    if (scopeErrors[field]) setScopeErrors((p) => ({ ...p, [field]: '' }));
  }

  function handleEconFieldChange(field, value) {
    setEconFields((prev) => ({ ...prev, [field]: value }));
    if (econErrors[field]) setEconErrors((p) => ({ ...p, [field]: '' }));
  }

  // ── Scope validation ─────────────────────────────────────────────────────
  function validateScope() {
    const errs = { ...EMPTY_SCOPE_ERRORS };
    let valid = true;
    if (!scopeFields.planName.trim())  { errs.planName  = 'Plan name is required.';  valid = false; }
    if (!scopeFields.planCode.trim())  { errs.planCode  = 'Plan code is required.';  valid = false; }
    else if (scopeFields.planCode.length < 2) { errs.planCode = 'Plan code must be at least 2 characters.'; valid = false; }
    if (!scopeFields.roleId)           { errs.roleId    = 'Role is required.';        valid = false; }
    if (!scopeFields.segmentId)        { errs.segmentId = 'Segment is required.';     valid = false; }
    setScopeErrors(errs);
    return valid;
  }

  // ── Modifier draft helpers ───────────────────────────────────────────────
  function handleDraftChange(key, draft) {
    setModifierDrafts((prev) => ({ ...prev, [key]: draft }));
    // Editing clears confirmation -- CA must re-confirm after changes
    const modKey = key === 'revType' ? 'rev_type' : key;
    setConfirmedModifiers((prev) => { const next = new Set(prev); next.delete(modKey); return next; });
  }

  function handleConfirmModifier(modKey) {
    setConfirmedModifiers((prev) => new Set([...prev, modKey]));
  }

  function handleBandChange(idx, field, value) {
    setBandDrafts((prev) => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  }

  // ── Step navigation -- dynamic, based on visibleSteps ───────────────────
  function goToStep2() { setStep(2); }

  function goToStep3() {
    if (!validateScope()) return;
    setStep(3);
  }

  function goToStep4() { setStep(4); }  // Rate Curve (if visible)
  function goToStep5() { setStep(5); }  // Modifiers (if visible)

  function backToStep1() { setStep(1); }
  function backToStep2() { setStep(2); setSubmitError(null); }
  function backToStep3() { setStep(3); setSubmitError(null); }
  function backToStep4() { setStep(4); setSubmitError(null); }

  // Determine what "Next" does from Step 3 -- depends on whether Steps 4/5 exist
  function advanceFromStep3() {
    if (hasStep4) { goToStep4(); return; }
    if (hasStep5) { goToStep5(); return; }
    // No further steps -- deploy directly (handleDeploy called from Step3 submit button)
  }

  function advanceFromStep4() {
    if (hasStep5) { goToStep5(); return; }
    // No Step 5 -- deploy directly
  }

  // ── Submit deploy ────────────────────────────────────────────────────────
  const handleDeploy = useCallback(async () => {
    if (submitting) return;

    abortDeployRef.current?.abort();
    const controller = new AbortController();
    abortDeployRef.current = controller;

    setSubmitting(true);
    setSubmitError(null);

    try {
      // ── Assemble extended POST body (S888 T2-1) ──────────────────────────
      // Core fields (unchanged from v1.1.0)
      const body = {
        planCode:       scopeFields.planCode.trim(),
        planName:       scopeFields.planName.trim(),
        roleId:         Number(scopeFields.roleId),
        segmentId:      Number(scopeFields.segmentId),
        fiscalPeriodId: Number(scopeFields.fiscalPeriodId),
        ...(scopeFields.businessUnitId && scopeFields.businessUnitId.toString().trim()
          ? { businessUnitId: Number(scopeFields.businessUnitId) || scopeFields.businessUnitId.trim() }
          : {}),
        ...(econFields.oteMin      !== '' ? { oteMin:      Number(econFields.oteMin)      } : {}),
        ...(econFields.oteMidpoint !== '' ? { oteMidpoint: Number(econFields.oteMidpoint) } : {}),
        ...(econFields.oteMax      !== '' ? { oteMax:      Number(econFields.oteMax)      } : {}),
      };

      // Step 4 -- confirmed rate curve bands (if step was shown and bands exist)
      if (hasStep4 && bandDrafts.length > 0) {
        body.confirmedBands = bandDrafts.map((b) => ({
          bandSequence:  Number(b.bandSequence),
          fromPct:       Number(b.fromPct),
          toPct:         Number(b.toPct),
          payoutPct:     Number(b.payoutPct),
          interpolation: b.interpolation,
          isAccelerator: b.isAccelerator ?? false,
        }));
      }

      // Step 5 -- confirmed modifier values (if step was shown)
      if (hasStep5) {
        // RevType
        if (modFlags.hasRevType && confirmedModifiers.has('rev_type')) {
          body.confirmedRevTypeRates = Object.fromEntries(
            Object.entries(modifierDrafts.revType.rates).map(([code, v]) => [
              code,
              { payoutPct: Number(v.payoutPct), effectiveDate: v.effectiveDate },
            ])
          );
        }
        // Clawback
        if (modFlags.hasClawback && confirmedModifiers.has('clawback')) {
          body.confirmedClawback = {
            protectionWindowDays: Number(modifierDrafts.clawback.protectionWindowDays),
            clawbackPct:          Number(modifierDrafts.clawback.clawbackPct),
            effectiveFrom:        modifierDrafts.clawback.effectiveFrom,
            billingModel:         modifierDrafts.clawback.billingModel ?? 'monthly',
          };
        }
        // MBO
        if (modFlags.hasMbo && confirmedModifiers.has('mbo')) {
          const weights = {};
          (modifierDrafts.mbo.objectives ?? []).forEach((o) => {
            weights[o.mboCode] = Number(o.weight);
          });
          body.confirmedMboWeights = weights;
        }
        // Optional kicker
        if (kickerEnabled && confirmedModifiers.has('kicker')) {
          body.confirmedKicker = {
            enabled:       true,
            threshold:     Number(modifierDrafts.kicker.threshold),
            multiplier:    Number(modifierDrafts.kicker.multiplier),
            effectiveFrom: modifierDrafts.kicker.effectiveFrom,
          };
        } else {
          body.confirmedKicker = { enabled: false };
        }
        // Optional draw
        if (drawEnabled && confirmedModifiers.has('draw')) {
          body.confirmedDraw = {
            enabled:         true,
            drawType:        'non_recoverable',
            advanceAmount:   Number(modifierDrafts.draw.advanceAmount),
            advanceCurrency: modifierDrafts.draw.advanceCurrency ?? 'USD',
            effectiveFrom:   modifierDrafts.draw.effectiveFrom,
          };
        } else {
          body.confirmedDraw = { enabled: false };
        }
      }

      const response = await fetch(
        `/api/v1/template-library/packages/${encodeURIComponent(pkg.packageCode)}/deploy`,
        {
          method:      'POST',
          credentials: 'include',
          signal:      controller.signal,
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify(body),
        },
      );

      if (!response.ok) {
        let message = `Deploy failed (${response.status}).`;
        try {
          const errPayload = await response.json();
          const code = errPayload.code ?? errPayload.error_code ?? '';
          // Spec error codes: PLAN_CODE_DUPLICATE 400, ROLE_NOT_FOUND 400,
          // SEGMENT_NOT_FOUND 400, FISCAL_PERIOD_CLOSED 409, PACKAGE_NOT_FOUND 404
          if (code === 'PLAN_CODE_DUPLICATE')    message = `Plan code "${body.planCode}" is already in use. Choose a different plan code.`;
          else if (code === 'ROLE_NOT_FOUND')    message = 'The selected role was not found. Please refresh and try again.';
          else if (code === 'SEGMENT_NOT_FOUND') message = 'The selected segment was not found. Please refresh and try again.';
          else if (code === 'FISCAL_PERIOD_CLOSED') message = 'The current fiscal period is closed. Deploy is not permitted.';
          else if (code === 'PACKAGE_NOT_FOUND') message = 'This package could not be found. It may have been removed.';
          else message = errPayload.detail ?? errPayload.message ?? message;
        } catch { /* no JSON body */ }
        throw new Error(message);
      }

      const payload = await response.json();
      if (controller.signal.aborted) return;

      const planId = extractPlanId(payload);
      if (planId === null || planId === undefined) {
        throw new Error('Deploy succeeded but the response did not include a plan ID.');
      }

      // Success: call onDeploySuccess first (triggers library refetch for badge update),
      // then navigate to PlanWorkspace -- NOT back to /admin/plans/library (D-S864-2).
      // Bug fix S936: onDeploySuccess fires fetchPackages in PlanLibraryBrowser so the
      // Status badge reflects the new instance_count when user navigates back to the library.
      onDeploySuccess?.();
      navigate(`/admin/plans/${planId}`);

    } catch (err) {
      if (err.name !== 'AbortError') {
        setSubmitError(err.message || 'Deploy failed. Please try again.');
      }
    } finally {
      if (!controller.signal.aborted) setSubmitting(false);
    }
  }, [submitting, scopeFields, econFields, pkg.packageCode, navigate]);

  // ── Focus trap + Escape key ──────────────────────────────────────────────
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const timer = window.setTimeout(() => {
      dialogRef.current
        ?.querySelector('button, input, select, [tabindex]:not([tabindex="-1"])')
        ?.focus();
    }, 0);

    function onKeyDown(e) {
      if (e.key === 'Escape' && !submitting) { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) { e.preventDefault(); return; }
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [onClose, submitting]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'rgb(var(--color-bg) / 0.75)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Deploy Plan from Library"
        style={{
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '12px',
          border: '1px solid rgb(var(--color-border))',
          background: 'rgb(var(--color-surface))',
          boxShadow: '0 8px 32px rgb(0 0 0 / 0.12)',
        }}
      >
        {/* ── Modal header ─────────────────────────────────────────────── */}
        <div className="modifier-manager-header">
          <div className="modifier-manager-header-copy">
            <h3>Deploy from Library</h3>
            <p>
              {step === 1 && 'Review the package details before deploying.'}
              {step === 2 && 'Set the scope for this plan.'}
              {step === 3 && 'Review and adjust the certified OTE guidance.'}
              {step === 4 && 'Confirm the attainment rate curve for this plan.'}
              {step === 5 && 'Review and confirm modifier configuration.'}
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Close deploy modal"
            disabled={submitting}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* ── Step indicator -- dynamic from visibleSteps ──────────────── */}
        <StepIndicator currentStep={step} visibleSteps={visibleSteps} />

        {/* ── Step content (scrollable) ────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {step === 1 && (
            <Step1PackageConfirm pkg={pkg} onNext={goToStep2} onClose={onClose} />
          )}
          {step === 2 && (
            <Step2Scope
              fields={scopeFields}
              errors={scopeErrors}
              roles={roles}
              segments={segments}
              fiscalPeriods={fiscalPeriods}
              periodsLoading={periodsLoading}
              businessUnits={businessUnits}
              rolesLoading={rolesLoading}
              segmentsLoading={segmentsLoading}
              bizUnitsLoading={bizUnitsLoading}
              planCodeManuallyEdited={planCodeManuallyEdited}
              onFieldChange={handleScopeFieldChange}
              onPlanNameChange={handlePlanNameChange}
              onPlanCodeChange={handlePlanCodeChange}
              onBack={backToStep1}
              onNext={goToStep3}
              submitting={submitting}
            />
          )}
          {step === 3 && (
            <Step3Economics
              fields={econFields}
              errors={econErrors}
              certifiedDefaults={certifiedDefaults}
              onFieldChange={handleEconFieldChange}
              onBack={backToStep2}
              onNext={hasStep4 || hasStep5 ? advanceFromStep3 : null}
              onSubmit={!hasStep4 && !hasStep5 ? handleDeploy : null}
              submitting={submitting}
              submitError={!hasStep4 && !hasStep5 ? submitError : null}
            />
          )}
          {step === 4 && hasStep4 && (
            <Step4RateCurve
              bands={bandDrafts}
              onBandChange={handleBandChange}
              onBack={backToStep3}
              onNext={hasStep5 ? advanceFromStep4 : handleDeploy}
              submitting={submitting}
            />
          )}
          {step === 5 && hasStep5 && (
            <Step5Modifiers
              modifierDrafts={modifierDrafts}
              confirmedModifiers={confirmedModifiers}
              onDraftChange={handleDraftChange}
              onConfirm={handleConfirmModifier}
              hasRevType={modFlags.hasRevType}
              hasClawback={modFlags.hasClawback}
              hasMbo={modFlags.hasMbo}
              hasKicker={modFlags.hasKicker}
              hasDraw={modFlags.hasDraw}
              showBillingModel={modFlags.showBillingModel}
              kickerEnabled={kickerEnabled}
              drawEnabled={drawEnabled}
              onKickerToggle={setKickerEnabled}
              onDrawToggle={setDrawEnabled}
              onBack={hasStep4 ? backToStep4 : backToStep3}
              onNext={handleDeploy}
              submitting={submitting}
            />
          )}
        </div>

      </section>
    </div>
  );
}

/*
 * ---------------------------------------------------------------------------
 * Document reconciliation (mandatory per S872 MANDATORY DOCUMENT PRODUCTION RULE)
 * ---------------------------------------------------------------------------
 * Input  : PlanLibraryDeployModal.jsx v1.2.0 (S888) -- 2,087 lines
 * Output : PlanLibraryDeployModal.jsx v1.2.1 (S936 badge fix) -- ~2,097 lines
 * Delta  : +10L. Surgical str_replace patches only. No rewrite.
 * Changes S936 (bug fix):
 *   - Added onDeploySuccess optional prop to component signature
 *   - Call onDeploySuccess?.() immediately before navigate() on successful deploy
 *   - This triggers PlanLibraryBrowser.fetchPackages() so instance_count refreshes
 *     and Status badge shows "Active · N instances" when user navigates back to library
 * Method : Surgical str_replace patches on verbatim copy -- no rewrite
 * Delta  : ~+950 lines (Step4RateCurve, Step5Modifiers + 6 modifier sub-components,
 *           computeVisibleSteps, deriveModifierFlags, buildModifierDrafts, buildBandDrafts,
 *           dynamic StepIndicator, extended POST body, new state in main component)
 *
 * Changes S888 T2-1:
 *   ARCHITECTURE: Step count is now dynamic. visibleSteps array drives StepIndicator
 *     and all navigation. No hardcoded package conditionals anywhere (OD-8/OD-9/OD-10).
 *   OD-8: Step 4 (Rate Curve) shown iff layer1Config.rateCurve.bands is non-empty.
 *   OD-9: Step 5 (Modifiers) shown iff ≥1 modifier enabled in layer2ConfigDefaults.
 *         Sub-panels rendered per enabled modifier type -- data-driven, not package-specific.
 *   OD-10: No Step 6 (RevType source mapping) -- tenant-level concern, Import Data surface.
 *          RevType-aware packages show awareness banner inside RevType modifier card.
 *   OD-7: RevType rates labelled "Recommended defaults — review and adjust for your
 *          selling motion" (plan_config_data_requirements_v1_0.md §21.8).
 *   Step 3: onNext/onSubmit split -- Next advances to Step 4/5 if they exist;
 *            Deploy fires directly if Step 3 is the final step.
 *   Step 4: Rate Curve -- editable band table (modifier-manager-table pattern).
 *            Bands pre-filled from pkg.layer1Config.rateCurve. CA adjusts, confirms.
 *   Step 5: Modifiers -- progressive disclosure card list.
 *            Pre-seeded modifiers: pre-checked, pre-filled, collapsed with summary.
 *            Optional (kicker, draw): unchecked, expand on check, confirm to lock.
 *            Next disabled until all pre-seeded modifiers confirmed (green check).
 *            Package 6 clawback: billing model selector drives protectionWindowDays.
 *            Draw: non-recoverable only (Bug 3 -- recoverable blocked until OI closes).
 *   POST body extended: confirmedBands, confirmedRevTypeRates, confirmedClawback,
 *            confirmedMboWeights, confirmedCap, confirmedKicker, confirmedDraw.
 *            All optional -- backend falls back to modifier_seed_config certified defaults.
 *   CSS: All named classes from index.css (S888 read). Zero hardcoded hex. Zero dark: variants.
 *        New classes used: modifier-manager-table-wrapper, modifier-manager-table,
 *        modifier-manager-percent-input, modifier-manager-badge--incomplete,
 *        modifier-manager-badge--complete, modifier-manager-info, modifier-manager-warning.
 *
 * Unchanged S888: Step 1, Step 2, focus trap, Escape key, abort pattern, error codes.
 * Authority: plan_config_data_requirements_v1_0.md §18.4, §21.8 · mastercontext S887 T2-1
 * Session: S888
 * ---------------------------------------------------------------------------
 */
